<template>
  <SwipeCell
    class="h5-carts-item-sku"
    :class="{
      'h5-carts-item-sku--processing': isProcessing(item.cartStatus),
      'h5-carts-item-sku--disabled': item.disabled
    }"
  >
    <div class="h5-carts-item-sku__wrapper">
      <div class="h5-carts-item-sku__left">
        <CartsSelect
          class="h5-carts-item-sku__select"
          :selected="item.checked"
          :disabled="item.disabled"
          @click="handleCheckClick"
        />
        <Image
          class="h5-carts-item-sku__image"
          :src="item.skuImageUrl"
          @click="showImagePreview({ images: [item.skuImageUrl] })"
        />
      </div>
      <div class="h5-carts-item-sku__content">
        <p class="h5-carts-item-sku__price">
          <IntlCurrency
            :currency="currency.current.targetCurrency"
          />
          <span>{{ unit }}</span>
          &nbsp;/ Pcs
        </p>
        <div class="h5-carts-item-sku__size">
          <p>
            {{ item.skuSpec }}
          </p>
        </div>
        <div class="h5-carts-item-sku__footer">
          <p class="h5-carts-item-sku__total">
            <IntlCurrency :currency="currency.current.targetCurrency" />
            {{ price }}
          </p>
          <NumberInput
            class="h5-carts-item-sku__input"
            v-model="item.quantity"
            :disabled-minus="item.disabled || item.quantity <= item.bundleQuantity"
            :disabled-plus="item.disabled"
            :min="1"
            :max="item.skuType === 2 ? 1 : undefined"
            :step="item.bundleQuantity"
          />
        </div>
      </div>
    </div>
    <p
      class="h5-carts-item-sku-error"
      v-if="item.unavailableMsg"
    >
      <Icon
        class="h5-carts-item-sku-error__icon"
        name="icon-warning-outline"
      />
      {{ item.unavailableMsg }}
      <span
        class="h5-carts-item-sku-error__contact"
        @click="contactUs()"
      >
        Contact us
      </span>
    </p>
    <template #right>
      <Button
        class="h5-carts-item-sku__delete"
        @click="handleDelete"
      >
        <Icon
          name="delete-trash"
          class="h5-carts-item-sku__delete-icon"
        />
        Delete
      </Button>
    </template>
  </SwipeCell>
</template>

<script setup>
import { Icon } from '@/components/Icon'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import NumberInput from '@/components/NumberInput/index.vue'
import { Image } from '@/components/Image'
import CartsSelect from '@/views/Carts/components/CartsSelect.vue'
import { SwipeCell } from '@/components/SwipeCell'
import { Button } from '@/components/Button'
import { useCart } from '@/views/Carts/hooks/useCart'
import { useCurrencyStore } from '@/pinia/currency'
import { useCartSku } from '@/views/Carts/hooks/useCartSku'
import { useContactApp } from '@/hooks/useContactApp'
import { showImagePreview } from '@/components/ImagePreview'
import { buriedPointUtil } from '@/utils/utils'

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
})

const { contactUs } = useContactApp()

const currency = useCurrencyStore()

const { isProcessing } = useCart()

const { unit, price, handleCheck, handleDelete } = useCartSku(computed(() => props.item))

const handleCheckClick = () => {
  buriedPointUtil({ eventId: 'HCL202', type: 'SKU' })
  handleCheck()
}
</script>

<style lang="less" scoped>
.h5-carts-item-sku {
  :deep(.c-swipe-cell__wrapper) {
    position: relative;
    padding: 8px 12px;
  }

  &__wrapper {
    display: flex;
    align-items: flex-start;
  }

  &--processing {
    background-color: #FFFCF7;

    :deep(.c-swipe-cell__wrapper) {
      &::after {
        content: 'Processing';
        position: absolute;
        top: 0;
        right: 12px;
        padding: 2px 6px;
        border-radius: 10px;
        color: #FFFFFF;
        background-color: #FFA033;
        font-family: mon-600;
        font-size: 10px;
        line-height: 14px;
      }
    }
  }

  &--disabled &__wrapper {
    filter: grayscale(1) opacity(.5);
  }

  &__left {
    display: flex;
    align-items: center;
  }

  &__select {
    flex-shrink: 0;
    font-size: 20px;
  }

  &__image {
    flex-shrink: 0;
    margin-left: 8px;
    width: 92px;
    height: 92px;
    border-radius: 8px;
    border: 1px solid #EBF1F5;
  }

  &__content {
    margin-left: 12px;
    flex: 1;
  }

  &__price {
    display: flex;
    align-items: center;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #5C5C5C;

    > span {
      font-family: mon-600;
    }
  }

  &__size {
    margin-top: 4px;
    padding: 6px 10px;
    width: fit-content;
    background-color: #F8F8F8;
    border-radius: 4px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #6E717A;

    > p {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 4;
    }
  }

  &__footer {
    margin-top: 7px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__total {
    font-family: mon-700;
    font-size: 14px;
    line-height: 18px;
  }

  &__input {
    width: 96px;
  }

  &-error {
    margin-top: 8px;
    padding-left: 28px;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    color: #7A0505;

    &__icon {
      font-size: 14px;
    }

    &__contact {
      margin-left: 6px;
      text-decoration: underline;
      color: #232323;
    }
  }

  &__delete {
    flex-direction: column;
    gap: 4px;
    width: 87px;
    height: 100%;
    border-radius: unset;
    background-color: #F54550;
    color: #FFFFFF;

    &-icon {
      font-size: 20px;
    }
  }
}
</style>
